import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { IconButton, InputAdornment } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';

import DialogModal from '../../../../components/DialogModal';
import VideoModal from '../VideoModal/VideoModal';
import { StyledButton } from '../../../../components/Button';
import LoadingBadge from '../../../../components/LoadingBadge';
import useToast from '../../../hooks/useToast';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';
import onbordingURLs from '../onboardingURLs.json';

import {
  Container,
  Title,
  StyledTextField,
  Subtitle,
  HelpText,
} from './styles';

import texts from './texts.json';

const ContentURLModal = ({
  coachId,
  loggedInUserId,
  loggedInUserName,
  coachAnswers,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [transformationPostUrl, setTransformationPostUrl] = useState('');
  const [launchPostUrl, setLaunchPostUrl] = useState('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [transformationPostHistoryDoc, setTransformationPostHistoryDoc] = useState({});
  const [launchPostHistoryDoc, setLaunchPostHistoryDoc] = useState({});

  const isCoachLeadView = !!(coachAnswers && !!Object.keys(coachAnswers).length);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchHistory = async () => {
      const [transformationPostHistory, launchPostHistory] = await Promise.all([
        CoachHistory.getCoachHistoryByCoachIdAndType(coachId, HistoryEventType.TRANSFORMATION_POST_SUBMITTED),
        CoachHistory.getCoachHistoryByCoachIdAndType(coachId, HistoryEventType.LAUNCH_POST_SUBMITTED),
      ]);
      if (transformationPostHistory) {
        setTransformationPostHistoryDoc(transformationPostHistory);
        setTransformationPostUrl(transformationPostHistory.metadata.transformationPostUrl || '');
      }
      if (launchPostHistory) {
        setLaunchPostHistoryDoc(launchPostHistory);
        setLaunchPostUrl(launchPostHistory.metadata.launchPostUrl || '');
      }
    };
    if (isComponentMountedRef.current) {
      if (coachAnswers && Object.keys(coachAnswers).length > 0) {
        setTransformationPostUrl(coachAnswers.transformationPostUrl || '');
        setLaunchPostUrl(coachAnswers.launchPostUrl || '');
      } else {
        fetchHistory();
      }
    }
  }, [coachAnswers, coachId, isComponentMountedRef]);

  const handleCopyUrl = useCallback((url, fieldName) => {
    if (url) {
      navigator.clipboard.writeText(url);
      showToast(format(texts.urlCopiedToClipboard, { fieldName }));
    }
  }, [showToast]);

  const handleOpenUrl = useCallback((url) => {
    if (url) {
      window.open(url, '_blank');
    }
  }, []);

  const renderInputAdornment = (url, fieldName) => (
    <InputAdornment position="end">
      <IconButton
        onClick={() => handleCopyUrl(url, fieldName)}
        disabled={!url}
        size="small"
        title={texts.copyUrl}
      >
        <ContentCopy fontSize="small" />
      </IconButton>
      <IconButton
        onClick={() => handleOpenUrl(url)}
        disabled={!url}
        size="small"
        title={texts.openUrl}
      >
        <OpenInNew fontSize="small" />
      </IconButton>
    </InputAdornment>
  );

  const handleSave = useCallback(async () => {
    if (!transformationPostUrl && !launchPostUrl) {
      showToast(texts.errorAllFieldsEmpty, { error: true });
      return;
    }

    try {
      setIsLoading(true);
      // Determine which history types to add based on submitted URLs
      const historyItemsToAdd = [];

      if (transformationPostUrl
        && transformationPostHistoryDoc?.metadata?.transformationPostUrl !== transformationPostUrl) {
        historyItemsToAdd.push({
          type: HistoryEventType.TRANSFORMATION_POST_SUBMITTED,
          coachId,
          actionedBy: loggedInUserId,
          actionedByUserName: loggedInUserName,
          metadata: { transformationPostUrl },
        });
      }

      if (launchPostUrl
        && launchPostHistoryDoc?.metadata?.launchPostUrl !== launchPostUrl) {
        historyItemsToAdd.push({
          type: HistoryEventType.LAUNCH_POST_SUBMITTED,
          coachId,
          actionedBy: loggedInUserId,
          actionedByUserName: loggedInUserName,
          metadata: { launchPostUrl },
        });
      }

      // If both URLs are submitted, also add the combined CONTENT_URLS_SUBMITTED event
      if (transformationPostUrl && launchPostUrl) {
        historyItemsToAdd.push({
          type: HistoryEventType.CONTENT_URLS_SUBMITTED,
          coachId,
          actionedBy: loggedInUserId,
          actionedByUserName: loggedInUserName,
          metadata: {
            transformationPostUrl,
            launchPostUrl,
          },
        });
      }

      // Add all history items
      await Promise.all(
        historyItemsToAdd.map((historyItem) => CoachHistory.addCoachHistoryItem(historyItem)),
      );

      if (isComponentMountedRef.current) {
        showToast(texts.contentURLsUpdatedSuccessfully);
        onClose();
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(format(texts.errorSavingContentURLs, { error: error.message }), { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    coachId,
    transformationPostUrl,
    launchPostUrl,
    loggedInUserId,
    loggedInUserName,
    isComponentMountedRef,
    showToast,
    onClose,
    transformationPostHistoryDoc,
    launchPostHistoryDoc,
  ]);

  return (
    <>
      <DialogModal
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Title>{texts.title}</Title>
          {!isCoachLeadView && (
            <>
              <Subtitle>{texts.subTitle}</Subtitle>
              <HelpText onClick={() => setIsVideoModalOpen(true)}>
                {texts.helpText}
              </HelpText>
            </>
          )}
          <StyledTextField
            label={texts.transformationPost}
            variant="outlined"
            value={transformationPostUrl}
            onChange={(e) => setTransformationPostUrl(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isLoading || isCoachLeadView}
            placeholder={texts.placeholder}
            InputProps={{
              endAdornment: isCoachLeadView
                ? renderInputAdornment(transformationPostUrl, texts.transformationPost)
                : null,
            }}
          />
          <StyledTextField
            label={texts.launchPost}
            variant="outlined"
            value={launchPostUrl}
            onChange={(e) => setLaunchPostUrl(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isLoading || isCoachLeadView}
            placeholder={texts.placeholder}
            InputProps={{
              endAdornment: isCoachLeadView
                ? renderInputAdornment(launchPostUrl, texts.launchPost)
                : null,
            }}
          />
          {!isCoachLeadView && (
            <StyledButton
              onClick={handleSave}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <LoadingBadge /> : texts.save}
            </StyledButton>
          )}
          {isVideoModalOpen
            && (
            <VideoModal
              isOpen={isVideoModalOpen}
              onClose={() => setIsVideoModalOpen(false)}
              videoUrl={onbordingURLs.contentURL}
              videoTitle={texts.videoTitle}
            />
            )}
        </Container>
      </DialogModal>
    </>
  );
};

ContentURLModal.propTypes = {
  coachId: PropTypes.string,
  loggedInUserId: PropTypes.string.isRequired,
  loggedInUserName: PropTypes.string.isRequired,
  coachAnswers: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

ContentURLModal.defaultProps = {
  coachId: undefined,
  coachAnswers: { transformationPostUrl: '', launchPostUrl: '' },
};

export default ContentURLModal;
